import { hash } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { WithBoundArgs } from '@glint/template';

import didUpdate from 'webapp/modifiers/did-update';
import type { RadiobuttonItemInfo } from 'webapp/components/form/radiobutton';
import SelectableListFiltersComponent from 'webapp/components/selectable-list/filters';
import SelectableListListComponent from 'webapp/components/selectable-list/list';

import FilterList from 'webapp/models-custom/filter-list';

// an option of the list, searched by its label (and email)
export interface SelectableListItem {
	label: string;
	value: string | number;
	email?: string;
	avatar?: string;
	isDisabled?: boolean;
}

interface Args {
	options: SelectableListItem[];
	values: SelectableListItem[];
	onItemSelectionChanged: (item: SelectableListItem, isSelected: boolean) => void;
	searchInProperties?: string[];
	filterOptions?: RadiobuttonItemInfo[];
	filterValue?: string;
	selectionListVisibleValues?: string;
	countTogglableOnly?: boolean;
	onFilterGroupChanged?: (filterValue: string) => void;
	onBeforeSelectionChange?: (isChecked: boolean) => Promise<void>;
	onDeselectAll?: () => void;
	onSelectAll?: () => void;
}

interface YieldedArgs {
	values: SelectableListItem[];
	filters: WithBoundArgs<
		typeof SelectableListFiltersComponent,
		'options' | 'value' | 'onFilterChanged'
	>;
	list: WithBoundArgs<
		typeof SelectableListListComponent,
		| 'filter'
		| 'selectedItems'
		| 'showList'
		| 'allSelected'
		| 'onSelectionChanged'
		| 'onBeforeSelectionChange'
		| 'onSelectAll'
		| 'onDeselectAll'
	>;
}

interface SelectableListComponentInterface {
	Args: Args;
	Blocks: {
		default: [settings: YieldedArgs];
	};
}

export default class SelectableListComponent extends Component<SelectableListComponentInterface> {
	listComponent = SelectableListListComponent;
	filterComponent = SelectableListFiltersComponent;

	@tracked typedFilter = '';
	@tracked filter!: FilterList<SelectableListItem>;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setupFilterList();
	}

	get searchInProperties(): string[] {
		return this.args.searchInProperties || ['label', 'email'];
	}

	get allSelected(): boolean {
		const allOptions = this.args.options || [],
			selectedOptions = this.args.values || [],
			allOptionsLength = this.args.countTogglableOnly
				? allOptions.filter((o) => !o.isDisabled).length
				: allOptions.length,
			selectedOptionsLength = this.args.countTogglableOnly
				? selectedOptions.filter((o) => !o.isDisabled).length
				: selectedOptions.length;

		return allOptionsLength === selectedOptionsLength;
	}

	get showSelectionList(): boolean {
		return (
			!this.args.selectionListVisibleValues ||
			(!!this.args.filterValue &&
				this.args.selectionListVisibleValues.includes(this.args.filterValue))
		);
	}

	resetTypedFilter(): void {
		this.filter.typedFilter = '';
	}

	@action
	setupFilterList(): void {
		this.filter = new FilterList({
			items: this.args.options,
			filterProperties: this.searchInProperties,
		});
	}

	@action
	filterGroupChanged(newSelection: AnySimpleValue): void {
		this.resetTypedFilter();
		if (this.args.onFilterGroupChanged && typeof newSelection === 'string') {
			this.args.onFilterGroupChanged(newSelection);
		}
	}
	<template>
		<div class="selectable-list" {{didUpdate this.setupFilterList @options}}>

			{{yield
				(hash
					values=@values
					filters=(component
						this.filterComponent
						options=@filterOptions
						value=@filterValue
						onFilterChanged=this.filterGroupChanged
					)
					list=(component
						this.listComponent
						filter=this.filter
						selectedItems=@values
						showList=this.showSelectionList
						allSelected=this.allSelected
						onSelectionChanged=@onItemSelectionChanged
						onBeforeSelectionChange=@onBeforeSelectionChange
						onSelectAll=@onSelectAll
						onDeselectAll=@onDeselectAll
					)
				)
			}}

		</div>
	</template>
}
